import { getCompanyRecordAuto, listCompanyRecordsAuto } from '../../../api/companyRecords';
import { normalizeWorkingSchedule } from '../employee-schedule/workingSchedule';
import {
  entityAssignedToLocation,
} from '../../../locations/locationTypes';
import {
  filterEmployeeIdsByLocation,
  filterStaffByLocation,
} from '../../../locations/filterByLocation';
import { resolveEffectiveCompanyHours } from '../../../locations/resolveLocationHours';

const normalizeIds = (ids) => {
  if (!Array.isArray(ids)) return [];
  return Array.from(
    new Set(
      ids
        .map((id) => String(id || '').trim())
        .filter(Boolean)
    )
  );
};

const sortStaffByName = (staff) => {
  return [...staff].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
};

const isOwnerRole = (role) => {
  const normalized = String(role || '').toLowerCase();
  return normalized === 'owner' || normalized === 'company_admin';
};

/** Shown in service assignment pickers — include team members unless archived. */
const isAssignableStaffMember = (staffData = {}) => {
  if (staffData.archived === true) return false;
  const status = String(staffData.status || 'active').toLowerCase();
  return status !== 'archived';
};

/** Used when resolving who can take bookings (stricter than assignment picker). */
const isBookableStaffMember = (staffData = {}, isOwner = false) => {
  if (!isAssignableStaffMember(staffData)) return false;
  if (staffData.active === false) return false;

  const status = String(staffData.status || '').toLowerCase();
  if (status === 'inactive') return false;

  if (isOwner) return true;
  return true;
};

const isOwnerEmployeeRecord = (employee, ownerId) => {
  if (!employee || !ownerId) return false;
  if (employee.id === ownerId) return true;
  if (String(employee.authId || '').trim() === ownerId) return true;
  if (isOwnerRole(employee.role)) return true;
  return false;
};

const formatStaffName = (firstName, lastName, fallback = 'Staff Member') => {
  const name = `${firstName || ''} ${lastName || ''}`.trim();
  return name || fallback;
};

const formatStaffTitle = (staffData = {}, isOwner = false) => {
  if (staffData.title) return staffData.title;
  if (isOwner || isOwnerRole(staffData.role)) return 'Owner';
  const role = String(staffData.role || '').toLowerCase();
  if (role === 'manager') return 'Manager';
  if (role === 'employee') return 'Staff Member';
  return 'Staff Member';
};

export const fetchCompanyStaff = async ({ companyId, ownerId, locationId = null }) => {
  if (!companyId) return [];

  const employees = await listCompanyRecordsAuto({ companyId, kind: 'employees' });
  const staffById = new Map();

  employees.forEach((employeeData) => {
    if (!employeeData?.id || !isAssignableStaffMember(employeeData)) return;
    const isOwner = isOwnerEmployeeRecord(employeeData, ownerId);
    staffById.set(employeeData.id, {
      ...employeeData,
      isOwner,
      name: formatStaffName(
        employeeData.firstName,
        employeeData.lastName,
        isOwner ? 'Owner' : 'Staff Member'
      ),
      title: formatStaffTitle(employeeData, isOwner),
      photoURL: employeeData.photoURL || employeeData.photoUrl || null,
      workingSchedule: employeeData.workingSchedule || null,
    });
  });

  const staff = sortStaffByName(Array.from(staffById.values()));
  return locationId ? filterStaffByLocation(staff, locationId) : staff;
};

export const getServiceStaffAssignment = async ({
  companyData,
  serviceId,
  locationId = null,
  location = null,
}) => {
  const ownerId = companyData?.ownerId || companyData?.createdBy;
  if (!companyData?.id || !serviceId) {
    return {
      serviceData: null,
      staff: [],
      eligibleEmployeeIds: [],
      explicitEmployeeIds: [],
      companyStaffAtLocation: [],
    };
  }

  const [serviceData, companyStaff] = await Promise.all([
    getCompanyRecordAuto({ companyId: companyData.id, kind: 'services', id: serviceId }),
    fetchCompanyStaff({ companyId: companyData.id, ownerId, locationId }),
  ]);

  if (!serviceData) {
    throw new Error('Service not found');
  }

  const explicitEmployeeIds = normalizeIds(serviceData.employeeIds);

  if (locationId && !entityAssignedToLocation(serviceData, locationId)) {
    return {
      serviceData,
      staff: [],
      eligibleEmployeeIds: [],
      explicitEmployeeIds,
      companyStaffAtLocation: companyStaff,
    };
  }

  const eligibleEmployeeIds = locationId
    ? filterEmployeeIdsByLocation(explicitEmployeeIds, companyStaff, locationId)
    : explicitEmployeeIds;
  const staffById = new Map(companyStaff.map((staffMember) => [staffMember.id, staffMember]));
  const effectiveCompanyHours = resolveEffectiveCompanyHours({
    location,
    companyData,
    locationId,
  });

  const staff = eligibleEmployeeIds
    .map((employeeId) => staffById.get(employeeId))
    .filter(Boolean)
    .map((staffMember) => ({
      ...staffMember,
      workingSchedule: normalizeWorkingSchedule(
        staffMember.workingSchedule,
        effectiveCompanyHours
      ),
    }));

  return {
    serviceData,
    staff,
    eligibleEmployeeIds,
    explicitEmployeeIds,
    companyStaffAtLocation: companyStaff,
  };
};
